import React, { useMemo } from 'react';
import { Tab } from '../Tab/v1';
import { Intake } from '../Intake/v1';
import { InProgress } from '../InProgress/v1';
import { Uploads } from '../Uploads/v1';
import { Emails } from '../Emails/v1';
import { Notes } from '../Notes/v1';
import { History } from '../History/v1';
import { Tasks } from '../Tasks/v1';
import {
  TRUE,
  INTAKE,
  TAB_DETAILS_SCREEN_RESIZE_DELAY,
  PRODUCTS_TABS_WRAPPER_SELECTOR,
  TABS_WRAPPER_SELECTOR,
  TABS_DETAILS_SELECTOR,
  PAGE_HEADING_SELECTOR,
  ORDER_DETAILS_SELECTOR,
} from '../../../constants/patientChart.constant';
import { FEATURE } from '../../../utils/feature';
import { useTabDetailsInlineHeight as useInlineHeight } from '../../../hooks/useTabDetailsInlineHeight';

const hasFeature = FEATURE();

const NotesComponent = new Map([
  [true, Notes],
  [false, InProgress],
]);

const EmailsComponent = new Map([
  [true, Emails],
  [false, InProgress],
]);

const HistoryComponent = new Map([
  [true, History],
  [false, InProgress],
]);

const TasksComponent = new Map([
  [true, Tasks],
  [false, InProgress],
]);

const TaskTab = {
  key: 'tasks',
  label: 'Tasks (0)',
  component: TasksComponent.get(hasFeature.TASKS_TAB),
};

export const Tabs = [
  {
    key: 'intake',
    label: 'Intake',
    component: Intake,
  },
  TaskTab,
  {
    key: 'notes',
    label: 'Notes',
    component: NotesComponent.get(hasFeature.NOTES_TAB),
  },
  {
    key: 'uploads',
    label: 'Uploads',
    component: Uploads,
  },
  {
    key: 'history',
    label: 'History',
    component: HistoryComponent.get(hasFeature.HISTORY_TAB),
  },
  {
    key: 'email',
    label: 'Email',
    component: EmailsComponent.get(hasFeature.EMAILS_TAB),
  },
];

export const TabsCollection = new Map([
  [Tabs[0].key, Tabs[0]],
]);

Tabs.forEach((tab) => {
  TabsCollection.set(tab.key, tab);
});

/**
 * @param {{
 *    tab: string;
 *    noOfPendingTasks: number;
 *    onClick: () => void;
 * }} props 
 * @returns {React.JSX.Element}
 */
export const TabsWrapper = ({
  tab = '',
  noOfPendingTasks = 0,
  onClick: handleTabClick = () => {}
}) => {
  const tabs = useMemo(() => {
    return Tabs.map((_tab) => {
      const tab = { ..._tab };
      if (tab.key === TaskTab.key) {
        tab.label = `Tasks (${noOfPendingTasks})`;
      }
      TabsCollection.set(tab.key, tab);
      return tab;
    });
  }, [noOfPendingTasks]);

  return (
    <div className='flex scroll-y-auto patient-chart-info-section-b-products-tab-wrapper p-b-3'>
      {
        tabs.map(({ key, label }, index) => (
          <Tab
            selected={tab === key}
            isFirst={index === 0}
            key={key}
            name={key}
            label={label}
            onClick={handleTabClick}
          />
        ))
      }
    </div>
  );
}

export const TabDetails = ({
  tab: _tab = '',
  data = {
    orderId: '',
    intake: INTAKE(),
    patientUploads: {},
    emailMessages: {},
    notes: {},
    notesAPI: {},
    tasksInfo: {},
  },
  action = {
    updateTask: () => ({}),
  },
}) => {
  const tab = TabsCollection.get(_tab);

  useInlineHeight({
    TRUE,
    delay: TAB_DETAILS_SCREEN_RESIZE_DELAY,
    selector: {
      targetElement: TABS_DETAILS_SELECTOR,
    },
    totalNoOfGenericPaddings: 2.5,
    selectors: [
      PAGE_HEADING_SELECTOR,
      ORDER_DETAILS_SELECTOR,
      PRODUCTS_TABS_WRAPPER_SELECTOR,
      TABS_WRAPPER_SELECTOR,
    ],
  });

  const tabs = [...TabsCollection.entries()].map(([key, { component: Component }]) => <Component key={key} tabKey={key} selectedKey={tab.key} data={data} action={action} />);

  return (
    <div className='flex flex-fill patient-chart-info-section-b-tab-details-column-a'>
      {tabs}
    </div>
  );
};

export default TabsWrapper;
